<script lang="ts">
  /**
   * The result of one check.
   *
   * Cases are grouped by the definitions they exercise, so a level that asks
   * for five names does not read as one flat pile of results: each group names
   * what its cases are about, how many of them passed and what they cost.
   *
   * A case says which terms it compared as well as what it found, since a label
   * like "a countdown driven by fix ends at zero" says what the law is about but
   * not which terms were compared. A case that did not hold opens that detail by
   * itself, because that is when it matters most.
   */
  import type { CaseSpec } from '@spec';
  import type { CheckReport } from '../engine/protocol';
  import { outcomeSummary, reportVerdict, verdictClassOf, verdictOf } from '../lib/format';
  import type { CaseGroup } from '../state/slots';

  interface Props {
    report: CheckReport;
    cases: CaseSpec[];
    groups: CaseGroup[];
    /** Called when the list is opened, so the report can be brought into view. */
    onopen?: () => void;
  }

  let { report, cases, groups, onopen }: Props = $props();

  /** Whether the cases are shown at all. A report opens its own. */
  let listed = $state(true);
  /** The cases whose detail is open, by id. A failing one starts open. */
  let open = $state(new Set<string>());

  $effect(() => {
    // A report arrives because the player asked for a check, so what it found
    // is worth showing; the list is here to be folded away afterwards.
    void report;
    listed = true;
  });

  /** Folds the cases away, and brings them back into view when they return. */
  function toggleList(): void {
    listed = !listed;
    if (listed) {
      onopen?.();
    }
  }

  const caseOf = (id: string): CaseSpec | undefined => cases.find((entry) => entry.id === id);
  const labelOf = (id: string, fallback: string): string => caseOf(id)?.label ?? fallback;

  /** The terms a case compares, as the level wrote them. */
  function tested(id: string): string[] {
    const spec = caseOf(id);
    return spec === undefined ? [] : [spec.lhs, spec.rhs, ...(spec.also ?? [])];
  }

  /**
   * A term as it is shown: a name the harness made up for itself stands for any
   * term at all, and reads better without the marking.
   */
  const shown = (term: string): string => term.replaceAll('__h_', '');

  const isOpen = (entry: CheckReport['cases'][number]): boolean =>
    open.has(entry.id) || (entry.outcome.status === 'fail' && !closed.has(entry.id));

  /** Cases whose detail a press has closed again, so they stay closed. */
  let closed = $state(new Set<string>());

  function toggle(id: string): void {
    const entry = report.cases.find((one) => one.id === id);
    if (entry === undefined) {
      return;
    }
    // Both sets are replaced rather than changed in place: a set is not
    // reactive by itself, so a change inside one would not redraw anything.
    if (isOpen(entry)) {
      closed = new Set([...closed, id]);
      open = new Set([...open].filter((one) => one !== id));
    } else {
      open = new Set([...open, id]);
      closed = new Set([...closed].filter((one) => one !== id));
    }
  }

  const totalSteps = $derived(report.cases.reduce((sum, entry) => sum + entry.steps, 0));

  /**
   * What every count in the report leaves out.
   *
   * The engine shares as it reduces: a subterm it has already reduced is not
   * reduced a second time, and the counter charges the work it did rather than
   * the work the term describes. Every count here carries the note, so none of
   * them can be read as a count of the redexes in the terms on the page.
   */
  const stepNote = '(excluding cache hits)';

  /**
   * What a number of steps means, for the hover text.
   *
   * The count is the work the checker did rather than anything about the
   * player's definitions, and it is counted in reductions, which is what the
   * engine spends a budget on.
   */
  const stepsMeaning =
    'How much work the checker did. One step is one beta or eta reduction, ' +
    'and a result the engine reused rather than computed again is not counted.';

  /** The report entries of a group: what to show, what passed, what it cost. */
  function entriesOf(group: CaseGroup): {
    visible: CheckReport['cases'];
    passed: number;
    total: number;
    steps: number;
  } {
    const all = group.indices
      .map((index) => report.cases[index])
      .filter((entry): entry is CheckReport['cases'][number] => entry !== undefined);
    return {
      visible: all,
      passed: all.filter((entry) => entry.outcome.status === 'pass').length,
      total: all.length,
      steps: all.reduce((sum, entry) => sum + entry.steps, 0),
    };
  }

  const passed = $derived(report.cases.filter((entry) => entry.outcome.status === 'pass').length);

  const verdict = $derived(reportVerdict(report));
  const verdictClass = $derived(verdictClassOf(verdict));
  const headers = $derived(groups.length > 1);
</script>

<section class="report">
  <!-- A report arrives without being asked for again, so it announces itself. -->
  <!-- The verdict line is the header of the list, so the whole line folds it. -->
  <header aria-live="polite">
    <button class="headline" aria-expanded={listed} title={listed ? 'Hide the cases' : 'Show the cases'} onclick={toggleList}>
      <span class="chevron" class:open={listed} aria-hidden="true"></span>
      <strong class="verdict {verdictClass}">{verdict}</strong>
      <span class="score">{passed} / {report.cases.length} cases passed</span>
      <span class="steps" title={stepsMeaning}>{totalSteps} reduction steps total {stepNote}</span>
    </button>
  </header>

  {#if listed}
    {#each groups as group, position (position)}
      {@const summary = entriesOf(group)}
      {#if summary.visible.length > 0}
      {#if headers}
        <h3>
          <span class="names">{group.names.length === 0 ? 'other' : group.names.join(' · ')}</span>
          <span class="group-steps" title={stepsMeaning}>{summary.steps} steps {stepNote}</span>
          <span class="tally" class:complete={summary.passed === summary.total}>
            {summary.passed}/{summary.total}
          </span>
          </h3>
        {/if}
        <ul>
        {#each summary.visible as entry (entry.id)}
          <li class={entry.outcome.status}>
            <button
              class="line"
              aria-expanded={isOpen(entry)}
              title="What this case checked"
              onclick={() => toggle(entry.id)}
            >
              <span class="case-verdict">{verdictOf(entry.outcome)}</span>
              <span class="label">{labelOf(entry.id, entry.label)}</span>
              <span class="steps" title={stepsMeaning}>{entry.steps} steps {stepNote}</span>
              <span class="caret" aria-hidden="true">{isOpen(entry) ? '▾' : '▸'}</span>
            </button>
            <p class="summary">{outcomeSummary(entry.outcome)}</p>

            {#if isOpen(entry)}
              <div class="detail">
                <div class="tested">
                  <span class="tag">{tested(entry.id).length > 2 ? 'terms' : 'tested'}</span>
                  {#if tested(entry.id).length > 2}
                    <span class="terms">
                      {#each tested(entry.id) as term, position (position)}
                        <code>{shown(term)}</code>{position < tested(entry.id).length - 1 ? ', ' : ''}
                      {/each}
                    </span>
                  {:else}
                    <code>{shown(tested(entry.id)[0] ?? '')}</code>
                    <span class="equals">=</span>
                    <code>{shown(tested(entry.id)[1] ?? '')}</code>
                  {/if}
                </div>
                <div>
                  <span class="tag">cost</span>
                  <span>{entry.steps} reduction steps {stepNote}</span>
                </div>
                {#if entry.outcome.status === 'fail' && entry.outcome.detail.kind === 'not_equal'}
                  <div>
                    <span class="tag">left reduced to</span> <code>{entry.outcome.detail.lhs}</code>
                  </div>
                  <div>
                    <span class="tag">right reduced to</span> <code>{entry.outcome.detail.rhs}</code>
                  </div>
                {/if}
                {#if entry.outcome.status === 'fail' && entry.outcome.detail.kind === 'not_distinct'}
                  <div>
                    <span class="tag">the same</span>
                    <code>{entry.outcome.detail.first}</code> and
                    <code>{entry.outcome.detail.second}</code>
                  </div>
                  <div>
                    <span class="tag">both are</span>
                    <code>{entry.outcome.detail.normalForm}</code>
                  </div>
                {/if}
              </div>
            {/if}
          </li>
        {/each}
        </ul>
      {/if}
    {/each}
  {/if}
</section>

<style>
  .report {
    margin-top: 18px;
  }

  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border);
  }

  /*
   * The disclosure mark is drawn rather than typed: the ▸ character's ink
   * depends on which font renders it, and a triangle of its own has a size
   * that does not move under it.
   */
  .chevron {
    display: inline-block;
    width: 0;
    height: 0;
    border-left: 5px solid var(--text-faint);
    border-top: 4.5px solid transparent;
    border-bottom: 4.5px solid transparent;
    transition: transform 0.12s ease-out;
  }

  .chevron.open {
    transform: rotate(90deg);
  }

  h3 {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin: 16px 0 2px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-faint);
  }

  .names {
    font-family: var(--mono);
    text-transform: none;
    letter-spacing: 0;
    overflow-wrap: anywhere;
  }

  .tally {
    font-family: var(--mono);
    flex: none;
  }

  .tally.complete {
    color: var(--pass);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
  }

  .line {
    display: flex;
    align-items: baseline;
    /*
     * A case label is the thing being read, and the counts beside it are a
     * note; in a narrow window the note moves to its own line rather than
     * squeezing the label into a column one letter wide.
     */
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .line:hover .label {
    color: var(--text);
  }

  .steps {
    flex: none;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-faint);
  }

  .caret {
    flex: none;
    color: var(--text-faint);
    font-size: 10px;
  }

  .group-steps {
    font-family: var(--mono);
    color: var(--text-faint);
    margin-left: auto;
    padding-right: 10px;
  }

  .detail {
    margin: 6px 0 0 1.6em;
    display: grid;
    gap: 3px;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .detail .equals {
    color: var(--text-faint);
    padding: 0 4px;
  }

  .detail code {
    font-family: var(--mono);
  }

  .terms code {
    margin-right: 2px;
  }

  .verdict {
    font-size: 0.95rem;
  }

  .verdict.ok {
    color: var(--pass);
  }

  .verdict.wrong-answer,
  .verdict.compilation-error {
    color: var(--fail);
  }

  .verdict.iteration-limit-exceeded {
    color: var(--warn);
  }

  .headline {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    background: none;
    border: none;
    border-radius: 4px;
    padding: 4px 6px;
    margin: 0 -6px;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  /*
   * The hover is the tint the editor gives the row the caret is on: quiet in
   * both palettes, and the mark deepens with it.
   */
  .headline:hover {
    background: var(--code-line);
  }

  .headline:hover .chevron {
    border-left-color: var(--text-dim);
  }

  .headline .chevron {
    /* The row is one line tall, so centering the mark in it is where an
     * arrow belongs; a glyph on the text baseline reads as low. */
    align-self: center;
  }

  .score {
    color: var(--text-dim);
  }

  .case-verdict {
    flex: none;
    font-weight: 700;
  }

  li.pass .case-verdict {
    color: var(--pass);
  }

  li.fail .case-verdict,
  li.error .case-verdict {
    color: var(--fail);
  }

  li.limit_exceeded .case-verdict {
    color: var(--warn);
  }

  .label {
    font-family: var(--mono);
    font-size: 13px;
    flex: 1;
    overflow-wrap: anywhere;
  }

  @media (max-width: 560px) {
    .line {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-areas:
        'verdict label caret'
        '. steps steps';
      align-items: baseline;
      column-gap: 8px;
      row-gap: 2px;
    }

    .case-verdict {
      grid-area: verdict;
    }

    .label {
      grid-area: label;
    }

    /* The total is the longest count in the report, so on the narrowest phones
     * it takes two lines rather than sticking out of the page. */
    .headline .steps {
      flex: 0 1 auto;
      min-width: 0;
    }

    .line .steps {
      grid-area: steps;
      /* A count plus its note is wider than the column on a 320 pixel screen,
       * so it may wrap rather than stick out of the row. */
      flex: 0 1 auto;
      min-width: 0;
    }

    .caret {
      grid-area: caret;
      justify-self: end;
    }
  }

  .summary {
    margin: 2px 0 0;
    color: var(--text-dim);
    font-size: 13px;
  }

  .counterexample {
    margin: 6px 0 0 1.6em;
    display: grid;
    gap: 3px;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .tag {
    display: inline-block;
    min-width: 5.5em;
    color: var(--text-faint);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
</style>
